import { useCallback, useEffect, useState } from "react";
import { getWorkflowAccessToken, isAuthConfigured, loginRequest, msal, reviewerPreviewSandboxEnabled, workflowEnabled } from "./auth";
import { getWorkflowIdentity } from "./workflow-api";
import { WorkflowDialog } from "./WorkflowPanel";
import ReviewerHcWorkflow, { useReviewerHcWorkflow } from "./ReviewerHcWorkflow";

function message(error) {
  return error instanceof Error ? error.message : String(error);
}

function Screen({ title, description, action, onAction, error }) {
  return <main className="auth-shell"><section className="auth-card">
    <img className="auth-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
    <p className="eyebrow">Profile Picture Studio</p>
    <h1>{title}</h1>
    {description && <p className="auth-description">{description}</p>}
    {action && <button className="auth-button" onClick={onAction}>{action}</button>}
    {error && <p className="auth-error">{error}</p>}
    <a href="/">Back to Studio</a>
  </section></main>;
}

export default function ReviewerApp() {
  const [status, setStatus] = useState("loading");
  const [account, setAccount] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!workflowEnabled || !isAuthConfigured || !msal) {
      setStatus("configuration");
      return;
    }
    let active = true;
    (async () => {
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const signedIn = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        if (signedIn) msal.setActiveAccount(signedIn);
        if (!active) return;
        setAccount(signedIn);
        setStatus(signedIn ? "authenticated" : "anonymous");
      } catch (authError) {
        if (!active) return;
        setError(message(authError));
        setStatus("anonymous");
      }
    })();
    return () => { active = false; };
  }, []);

  const getAccessToken = useCallback(() => getWorkflowAccessToken(account), [account]);
  const ccdOnly = identity?.capabilities?.ccdApprover === true && identity?.capabilities?.reviewer !== true;
  const hcWorkflow = useReviewerHcWorkflow(getAccessToken, ccdOnly);

  useEffect(() => {
    if (status !== "authenticated" || !account) return;
    let active = true;
    setError("");
    getWorkflowIdentity(getAccessToken)
      .then((result) => {
        if (!active) return;
        const raw = result.identity;
        if (raw?.capabilities?.reviewer !== true && raw?.capabilities?.ccdApprover !== true) {
          setStatus("forbidden");
          return;
        }
        setIdentity(raw);
      })
      .catch((loadError) => { if (active) setError(message(loadError)); });
    return () => { active = false; };
  }, [status, account?.homeAccountId]);

  async function signIn() {
    setError("");
    try { await msal.loginRedirect(loginRequest); }
    catch (authError) { setError(message(authError)); }
  }

  async function signOut() {
    try { await msal.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin }); }
    catch (authError) { setError(message(authError)); }
  }

  if (status === "loading") return <Screen title="Opening Reviewer Portal…" />;
  if (status === "configuration") return <Screen title="Reviewer Portal unavailable" description="Workflow authentication is not configured." />;
  if (status === "anonymous") return <Screen title="Reviewer sign in" description="Use your Rawabi Microsoft account." action="Sign in with Microsoft" onAction={signIn} error={error} />;
  if (status === "forbidden") return <Screen title="Reviewer or CCD access required" description="The request register requires Reviewer permission, and the HC approver directory requires Reviewer or Central CCD approval permission." action="Switch account" onAction={signIn} error={error} />;

  return <main className="reviewer-app">
    <header className="reviewer-app-header">
      <div><p className="eyebrow">Profile Picture Studio</p><h1>Reviewer Portal</h1><small>{identity?.name || identity?.email}</small></div>
      <nav aria-label="Reviewer portal navigation">
        {identity?.capabilities?.adminSettings && <a href="/admin/">Admin Portal</a>}
        <a href="/">Studio</a>
        <button type="button" onClick={signOut}>Sign out</button>
      </nav>
    </header>
    {reviewerPreviewSandboxEnabled && <div className="workflow-notice reviewer-preview-banner" role="status"><strong>Preview environment</strong> — Production data is shown read-only for Reviewer validation. Preview write actions remain disabled; employee photos and Microsoft 365 are not modified.</div>}
    {error && <div className="workflow-error" role="alert">{error}</div>}
    {identity
      ? ccdOnly
        ? <div className="reviewer-portal-shell"><div className="reviewer-ccd-note"><strong>Central CCD approver view</strong><p>You can view current HC approvers by Business Unit. Request history, photos and approval permissions require their separate roles.</p></div><ReviewerHcWorkflow workflow={hcWorkflow} /></div>
        : <WorkflowDialog standalone identity={identity} getAccessToken={getAccessToken} />
      : !error && <p className="workflow-empty">Loading Reviewer or CCD access…</p>}
  </main>;
}
